import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {CardFileProps, CardType} from "../../types/types";
import Card from "../Card/Card";
import classes from "./CardFileComponent.module.css";
import {RAIL_VIEW} from "../../consts/viewType";
import {DEFAULT_CONFIG} from "../../consts/config";
import AddCard from "../AddCard/AddCard";
import EmptyCardFile from "../EmptyCardFile/EmptyCardFile";
import update from 'immutability-helper';
import DnDCard from "../DnDCard/DnDCard";
import {DndContext, DragEndEvent, DragOverlay, DragStartEvent} from "@dnd-kit/core";
import {arrayMove, SortableContext} from "@dnd-kit/sortable";
import {triggerEvent} from "../../utils/eventRegistry";
import type {DragCancelEvent, DragMoveEvent, DragOverEvent} from "@dnd-kit/core/dist/types";


const CardFileComponent: React.FC<CardFileProps> = (props) => {
    const [activeId, setActiveId] = useState<string | number | null>(null);

    const [filteredData, setFilteredData] = useState<CardType[]>(props.data);

    const config = useMemo(() => {
        return props.config ? props.config : DEFAULT_CONFIG;
    }, [props.config]);

    const viewClassName = useMemo(() => {
        return props.config.viewType === RAIL_VIEW ? classes.railCardFile : classes.tileCardFile;
    }, [props.config.viewType]);

    useEffect(() => {
        setFilteredData(
            (props.config.searchInput && props.config.filterFunc) ?
                props.data.filter(card => props.config.filterFunc!(props.config.searchInput!, card)) :
                props.data
        );
    }, [props.config.searchInput, props.data]);

    const moveCard = useCallback((dragIndex: number, hoverIndex: number) => {
        setFilteredData((prevCards: CardType[]) =>
            update(prevCards, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, prevCards[dragIndex] as CardType],
                ],
            }),
        );
    }, []);

    function handleDragStart(event: DragStartEvent) {
        const { active } = event;
        setActiveId(active.id);
        triggerEvent('OnCardDragStart', active);
    }

    function handleDragEnd(event: DragEndEvent) {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            props.updateSortedData(active.id, over.id);
            setFilteredData((items)=>{
                const oldIndex = items.findIndex((item) => item.id === active.id);
                const newIndex = items.findIndex((item) => item.id === over.id);
                return arrayMove(items, oldIndex, newIndex)
            });
        }
        setActiveId(null);
        triggerEvent('OnCardDragEnd', active, over);
    }

    function handleDragMove(event: DragMoveEvent) {
        const { active, over } = event;
        triggerEvent('OnCardDragMove', active, over);
    }

    function handleDragOver(event: DragOverEvent) {
        const { active, over } = event;
        triggerEvent('OnCardDragOver', active, over);
    }

    function handleDragCancel(event: DragCancelEvent) {
        const { active, over } = event;
        triggerEvent('OnCardDragCancel', active, over);
    }

    return (
        <div className={viewClassName}>
                {props.data.length > 0 ?
                    <DndContext onDragEnd={handleDragEnd} onDragStart={handleDragStart} onDragMove={handleDragMove}
                        onDragOver={handleDragOver} onDragCancel={handleDragCancel}>
                        <SortableContext items={filteredData}>
                        {
                            filteredData.map((card :CardType, index) => (
                            <DnDCard
                                key={card.id}
                                index={index}
                                id={card.id}
                                card={card}
                                config={config}
                                moveCard={moveCard}
                                className={card.id === activeId ? 'activeCardOverlay' : undefined}
                            />
                            ))
                        }
                        </SortableContext>
                        <DragOverlay>
                            {activeId ? (
                                (() => {
                                    const card = filteredData.find((item) => item.id === activeId);
                                    return card ? <Card card={card} config={config} className={'activeCard'}/> : null;
                                })()
                            ) : null}
                        </DragOverlay>
                        <AddCard config={config}/>
                    </DndContext>
                :
                    <div className={classes.emptyCardFile}>
                    <EmptyCardFile
                        header={config.emptyCardFile?.header ? config.emptyCardFile.header : DEFAULT_CONFIG.emptyCardFile!.header}
                        description={config.emptyCardFile?.description ? config.emptyCardFile.description : DEFAULT_CONFIG.emptyCardFile!.description}
                        imageSrc={config.emptyCardFile?.imageSrc ? config.emptyCardFile.imageSrc : DEFAULT_CONFIG.emptyCardFile!.imageSrc}/>
                    </div>
                }
        </div>
    );
};

export default CardFileComponent;